import { getDocs, query, where } from 'firebase/firestore'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { opticTruckBlue, logoDark } from '@/assets'
import { compareInventorySizesForPdf } from '@/constants/inventory'
import { db } from '@otw/models/db'
import { canonicalVehicleTier } from '@otw/models/inventory'

// Base inventory item interface with common properties
interface InventoryItem {
  stock_number: string
  make: string
  model: string
  size: string
  fuel: string
  miles: string
  year: string
  condition: string
  tier: string
  location: string
  type: string
  status: string
}

// Master inventory extends base with Optic pricing
interface MasterInventoryItem extends InventoryItem {
  optic_list_price: string
}

// Shield inventory extends base with seller information
interface ShieldInventoryItem extends InventoryItem {
  seller_asking_price: string
  who: string
  email: string
  phone_number: string
}

// Formatting helpers
const formatCurrency = (value: string | number | undefined): string => {
  if (value === undefined) return ''
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : value
  return isNaN(numValue) ? '' : numValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const formatNumber = (value: number | string): string => {
  const numValue = typeof value === 'string' ? parseInt(value.replace(/[^0-9-]+/g, ''), 10) : value
  return numValue.toLocaleString('en-US')
}

// Get nested value by path e.g. 'sold_to_user.name'
function getValueByPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.')
  let current: unknown = obj
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return current
}

// Format a cell value for known numeric/currency fields
function formatCellValue(field: string, value: unknown): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate: () => Date }).toDate().toLocaleDateString('en-US')
    } catch {
      return String(value)
    }
  }
  const currencyFields = ['optic_list_price', 'seller_asking_price', 'purchase_price']
  if (currencyFields.includes(field)) {
    return formatCurrency(value as string | number)
  }
  const numberFields = ['miles', 'year']
  if (numberFields.includes(field)) {
    return formatNumber(value as number | string)
  }
  if (field === 'tier' || field.endsWith('.tier')) {
    return canonicalVehicleTier(value) ?? ''
  }
  return String(value)
}

export type PdfColumnConfig = { colId: string; headerName: string; field: string }

// Column definitions for better readability
const COMMON_COLUMNS = [
  { header: 'Stock Number', key: 'stock_number', width: 16 },
  { header: 'Make', key: 'make', width: 15 },
  { header: 'Model', key: 'model', width: 20 },
  { header: 'Size', key: 'size', width: 15 },
  { header: 'Fuel', key: 'fuel', width: 10 },
  { header: 'Miles', key: 'miles', width: 15, align: 'left', format: formatNumber },
  { header: 'Year', key: 'year', width: 10, align: 'center' },
  { header: 'Condition', key: 'condition', width: 15 },
  { header: 'Tier', key: 'tier', width: 14 },
  { header: 'Location', key: 'location', width: 15 },
  { header: 'Type', key: 'type', width: 15 },
  { header: 'Status', key: 'status', width: 15 },
]

const MASTER_COLUMNS = [
  ...COMMON_COLUMNS,
  { header: 'Sale Price', key: 'optic_list_price', width: 20, align: 'left', format: formatCurrency },
]

const SHIELD_COLUMNS = [
  ...COMMON_COLUMNS,
  { header: 'Price', key: 'seller_asking_price', width: 20, align: 'left', format: formatCurrency },
  { header: 'Seller Name', key: 'who', width: 25 },
  { header: 'Email', key: 'email', width: 30 },
  { header: 'Phone', key: 'phone_number', width: 20 },
]

type TableColumn = {
  header: string
  key: string
  width?: number
  align?: string
  format?: (v: string | number) => string
}

const getItemSize = (item: Record<string, unknown> | InventoryItem): string =>
  String((item as InventoryItem).size ?? getValueByPath(item as Record<string, unknown>, 'size') ?? '')

const sortItemsBySizeForPdf = <T extends Record<string, unknown> | InventoryItem>(items: T[]): T[] =>
  [...items].sort((a, b) => compareInventorySizesForPdf(getItemSize(a), getItemSize(b)))

const convertToPDF = (
  items: (MasterInventoryItem | ShieldInventoryItem)[] | Record<string, unknown>[],
  columnConfig?: PdfColumnConfig[],
): jsPDF => {
  let columns: TableColumn[]
  let headers: string[]
  let rows: string[][]

  const sortedItems = sortItemsBySizeForPdf(items as (Record<string, unknown> | InventoryItem)[])

  if (columnConfig?.length) {
    // Use visible columns from grid: headers and field paths
    headers = columnConfig.map(c => c.headerName)
    rows = (sortedItems as Record<string, unknown>[]).map(item => {
      return columnConfig.map(col => {
        const value = getValueByPath(item, col.field)
        return formatCellValue(col.field, value)
      })
    })
    columns = columnConfig.map(c => ({
      header: c.headerName,
      key: c.field,
      width: 15,
    })) as TableColumn[]
  } else {
    const typedItems = sortedItems as (MasterInventoryItem | ShieldInventoryItem)[]
    const isMasterInventory = typedItems.length > 0 && 'optic_list_price' in typedItems[0]
    columns = (isMasterInventory ? MASTER_COLUMNS : SHIELD_COLUMNS) as TableColumn[]

    headers = columns.map(col => col.header)
    rows = typedItems.map(item => {
      return columns.map(col => {
        const value = item[col.key as keyof typeof item]
        if (col.format && value !== undefined) {
          return col.format!(value)
        }
        return (value as string) || ''
      })
    })
  }

  // Create PDF document
  const doc = new jsPDF({
    orientation: 'landscape', // Use landscape for more horizontal space
    unit: 'mm',
    format: 'a4',
    compress: true,
  })
  doc.setFontSize(10)

  // Get page dimensions
  const pageWidth = doc.internal.pageSize.width
  const pageHeight = doc.internal.pageSize.height

  // Define logo sizes and positions
  const topLogoSize = 24
  const topLogoY = 5
  const topLogoX = (pageWidth - topLogoSize) / 2

  const bottomLogoSize = 12
  const bottomLogoY = pageHeight - 18
  const bottomLogoX = (pageWidth - bottomLogoSize) / 2

  // Define table margins to avoid overlap with logos
  const tableTopMargin = topLogoY + topLogoSize + 5
  const tableBottomMargin = 25

  // Calculate available width for the table (accounting for page margins)
  const availableWidth = pageWidth - 20 // 10mm margin on each side

  // Calculate relative widths based on column definitions
  const totalDefinedWidth = columns.reduce((sum, col) => sum + (col.width || 10), 0)

  // Create dynamic column styles with proportional widths
  const columnStyles: Record<number, any> = {}
  columns.forEach((col, index) => {
    // Calculate proportional width to fill the entire available space
    const proportionalWidth = ((col.width || 10) / totalDefinedWidth) * availableWidth

    columnStyles[index] = {
      cellWidth: proportionalWidth,
      halign: col.align || 'left',
      overflow: 'linebreak',
      cellPadding: 1,
      fontSize: 7, // Smaller font size to ensure everything fits
    }
  })

  // Add top logo to the page
  const addTopLogo = (doc: jsPDF) => {
    doc.addImage(opticTruckBlue, 'PNG', topLogoX, topLogoY, topLogoSize, topLogoSize)
  }

  // Add bottom logo to the page
  const addBottomLogo = (doc: jsPDF) => {
    doc.addImage(logoDark, 'PNG', bottomLogoX, bottomLogoY, bottomLogoSize, bottomLogoSize)
  }

  let firstPage = true

  // Generate table
  doc.autoTable({
    head: [headers],
    body: rows,
    startY: tableTopMargin,
    theme: 'grid',
    headStyles: {
      fillColor: [52, 73, 94],
      textColor: 255,
      fontSize: 7, // Smaller font size for headers
      cellPadding: 1.5,
      lineWidth: 0.1,
      lineColor: [200, 200, 200],
      minCellHeight: 8,
      valign: 'middle',
    },
    styles: {
      fontSize: 7, // Smaller font size for all content
      cellPadding: 1.5,
      lineWidth: 0.1,
      lineColor: [200, 200, 200],
      minCellHeight: 7,
      overflow: 'linebreak',
      font: 'helvetica',
    },
    columnStyles: columnStyles,
    alternateRowStyles: { fillColor: [242, 242, 242] },
    margin: { left: 10, right: 10, bottom: tableBottomMargin },
    didDrawPage: (data: { pageNumber: number; settings: { startY: number }; pageCount: number }) => {
      // Add top logo only on first page
      if (data.pageNumber === 1) {
        addTopLogo(doc)
      }

      // Add bottom logo on all pages
      addBottomLogo(doc)

      // Adjust start position for content on subsequent pages
      if (firstPage) {
        firstPage = false
      } else {
        data.settings.startY = 15
      }
    },
    willDrawCell: (data: {
      column: { index: number }
      cell: { text: string[]; styles: { cellPadding: number; fontSize: number; overflow: string } }
      row: { index: number }
      section: string
    }) => {
      // Special handling for email column
      if (data.column.index === columns.findIndex(col => col.key === 'email')) {
        data.cell.styles.fontSize = 6 // Even smaller font for email addresses
      }

      // Ensure headers don't break mid-word
      if (data.section === 'head') {
        data.cell.styles.overflow = 'ellipsize'
      }
    },
    tableWidth: 'auto', // Let the table calculate its width based on content and available space
    bodyStyles: {
      maxCellHeight: pageHeight - tableTopMargin - tableBottomMargin,
    },
    horizontalPageBreak: false, // Disable horizontal page breaks
  })

  return doc
}

const mapVehicleToInventoryItem = (
  collectionName: 'master_inventory' | 'shield_inventory',
  vehicle: Record<string, unknown>,
): MasterInventoryItem | ShieldInventoryItem => {
  const baseItem: InventoryItem = {
    stock_number: (vehicle.stock_number as string) || '',
    make: (vehicle.make as string) || '',
    model: (vehicle.model as string) || '',
    size: (vehicle.size as string) || '',
    fuel: (vehicle.fuel as string) || '',
    miles: String(vehicle.miles ?? ''),
    year: String(vehicle.year ?? ''),
    condition: (vehicle.condition as string) || '',
    tier: canonicalVehicleTier(vehicle.tier) ?? '',
    location: (vehicle.location as string) || '',
    type: (vehicle.type as string) || '',
    status: (vehicle.status as string) || '',
  }

  if (collectionName === 'master_inventory') {
    return {
      ...baseItem,
      optic_list_price: (vehicle.optic_list_price as string) || '',
    } as MasterInventoryItem
  }

  return {
    ...baseItem,
    seller_asking_price: (vehicle.seller_asking_price as string) || '',
    who: (vehicle.who as string) || '',
    email: (vehicle.email as string) || '',
    phone_number: (vehicle.phone_number as string) || '',
  } as ShieldInventoryItem
}

const saveInventoryPdf = (
  collectionName: 'master_inventory' | 'shield_inventory',
  inventoryItems: (MasterInventoryItem | ShieldInventoryItem)[],
  rawItems: Record<string, unknown>[],
  columnConfig?: PdfColumnConfig[],
) => {
  if (inventoryItems.length === 0 && rawItems.length === 0) {
    throw new Error('No vehicles to export')
  }

  const doc =
    columnConfig?.length && rawItems.length > 0
      ? convertToPDF(rawItems, columnConfig)
      : convertToPDF(inventoryItems)

  const filename = collectionName === 'master_inventory' ? 'optic_inventory.pdf' : 'shield_inventory.pdf'
  doc.save(filename)
}

/** Build a PDF from vehicles already loaded in the UI (no Firestore re-fetch). */
export const downloadInventoryPdfFromVehicles = (
  collectionName: 'master_inventory' | 'shield_inventory',
  vehicles: Array<Record<string, unknown>>,
  columnConfig?: PdfColumnConfig[],
) => {
  if (columnConfig?.length) {
    saveInventoryPdf(
      collectionName,
      [],
      vehicles.map(vehicle => ({ ...vehicle })),
      columnConfig,
    )
    return
  }

  saveInventoryPdf(
    collectionName,
    vehicles.map(vehicle => mapVehicleToInventoryItem(collectionName, vehicle)),
    [],
  )
}

/** Fetch by id then download. Prefer downloadInventoryPdfFromVehicles when rows are already loaded. */
export const downloadShareableInventoryPdf = async (
  collectionName: 'master_inventory' | 'shield_inventory',
  ids: string[],
  columnConfig?: PdfColumnConfig[],
) => {
  const inventoryCollection = collectionName === 'master_inventory' ? db.master_inventory : db.shield_inventory

  const batchSize = 30
  const inventoryItems: (MasterInventoryItem | ShieldInventoryItem)[] = []
  const rawItems: Record<string, unknown>[] = []

  for (let i = 0; i < ids.length; i += batchSize) {
    const batchIds = ids.slice(i, i + batchSize)
    const inventoryQuery = query(inventoryCollection, where('__name__', 'in', batchIds))
    const querySnapshot = await getDocs(inventoryQuery)

    querySnapshot.forEach(docSnap => {
      const vehicle = docSnap.data() as unknown as Record<string, unknown>

      if (columnConfig?.length) {
        rawItems.push({ ...vehicle })
      } else {
        inventoryItems.push(mapVehicleToInventoryItem(collectionName, vehicle))
      }
    })
  }

  saveInventoryPdf(collectionName, inventoryItems, rawItems, columnConfig)
}
